import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Login from './components/Login';
import AdminDashboard from './pages/admin/Dashboard';
import PropertiesPage from './pages/admin/Properties';
import LeadsPage from './pages/admin/Leads';
import TransactionsPage from './pages/admin/Transactions';
import ReportsPage from './pages/admin/Reports';
import UsersPage from './pages/admin/Users';
import SellersPage from './pages/admin/Sellers';
import BannersPage from './pages/admin/Banners';
import PlansPage from './pages/admin/Plans';
import HelpdeskPage from './pages/admin/Helpdesk';
import HomestaysPage from './pages/admin/Homestays';
import DataEntryAddPropertyPage from './pages/dataentry/AddProperty';
import DataEntryAddAreaPage from './pages/dataentry/AddArea';
import ChatsPage from './pages/sales/Chats';
import ChatViewPage from './pages/sales/ChatView';
import Sidebar from './components/Sidebar';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

const InactivityMonitor = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    let timeout;
    const resetTimeout = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        logout();
        navigate('/login', { state: { reason: 'inactivity' } });
      }, 15 * 60 * 1000); // 15 minutes
    };

    const events = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];
    const handleEvent = () => resetTimeout();

    events.forEach(event => window.addEventListener(event, handleEvent));
    resetTimeout();

    return () => {
      clearTimeout(timeout);
      events.forEach(event => window.removeEventListener(event, handleEvent));
    };
  }, [user, logout, navigate]);

  return children;
};

const RoleRouter = () => {
  const { userProfile } = useAuth();
  if (!userProfile) return null;

  switch (userProfile.role) {
    case 'admin':
    case 'data_entry':
    case 'sales':
      return <AdminDashboard />;
    default:
      return <div className="p-8">Invalid role</div>;
  }
};

const RoleProtectedRoute = ({ allowedRoles, children }) => {
  const { userProfile, loading } = useAuth();

  if (loading || !userProfile) {
    return null;
  }

  if (!allowedRoles.includes(userProfile.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  return (
    <div className="flex min-h-screen items-stretch bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-h-screen lg:pl-0 pl-0">
        <div className="lg:hidden h-14" /> {/* Spacer for mobile hamburger */}
        {children}
      </div>
    </div>
  );
};

const LayoutRouter = ({ children }) => {
  const { userProfile } = useAuth();
  if (!userProfile) return null;

  if (['admin', 'data_entry', 'sales'].includes(userProfile.role)) {
    return <AppLayout>{children}</AppLayout>;
  }

  return <div className="bg-slate-50 min-h-screen">{children}</div>;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <InactivityMonitor>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><LayoutRouter><RoleRouter /></LayoutRouter></ProtectedRoute>} />
          <Route path="/add-property" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'data_entry']}><AppLayout><DataEntryAddPropertyPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/edit-property/:propertyId" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'data_entry']}><AppLayout><DataEntryAddPropertyPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />

          <Route path="/add-area" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'data_entry']}><AppLayout><DataEntryAddAreaPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/properties" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'data_entry', 'sales']}><AppLayout><PropertiesPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/homestays" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'data_entry', 'sales']}><AppLayout><HomestaysPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/leads" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'sales']}><AppLayout><LeadsPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/transactions" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'sales']}><AppLayout><TransactionsPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/chats" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'sales']}><AppLayout><ChatsPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/chats/:chatId" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'sales']}><AppLayout><ChatViewPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin']}><AppLayout><ReportsPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin']}><AppLayout><UsersPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/sellers" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'data_entry', 'sales']}><AppLayout><SellersPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/banners" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'data_entry']}><AppLayout><BannersPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/plans" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin']}><AppLayout><PlansPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
          <Route path="/helpdesk" element={<ProtectedRoute><RoleProtectedRoute allowedRoles={['admin', 'data_entry', 'sales']}><AppLayout><HelpdeskPage /></AppLayout></RoleProtectedRoute></ProtectedRoute>} />
        </Routes>
        </InactivityMonitor>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
